import React from "react";
import { toastManager } from "@salestrak/ui";

import { createDialogEmitter } from "#app/lib/dialog-emitter";
import { WhatsNewDialog } from "#app/components/navigation/whats-new-dialog";
import { useAnnouncements } from "#app/hooks/use-announcements";
import { usePersistedState } from "#app/lib/hooks/use-persisted-state";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useAuth } from "#app/lib/auth-context";
import { AnalyticsContext, AnnouncementEvent, trackEvent } from "#app/lib/analytics";

type WhatsNewDialogActions = {
  openDialog: () => void;
  closeDialog: () => void;
};

// Opened from the account menu and from the release toast's action, which
// lives outside the React tree that renders the dialog.
const whatsNewDialog = createDialogEmitter("whats_new");

/** Stable open/close callbacks. Works inside portalled overlays. */
export function useWhatsNewDialogActions(): WhatsNewDialogActions {
  return React.useMemo<WhatsNewDialogActions>(
    () => ({ openDialog: () => openWhatsNew("account_menu"), closeDialog: whatsNewDialog.close }),
    [],
  );
}

function openWhatsNew(via: "account_menu" | "toast") {
  trackEvent({
    context: AnalyticsContext.Announcement,
    event: AnnouncementEvent.Open,
    data: { via },
  });
  whatsNewDialog.open();
}

/**
 * Entry keys whose release toast the viewer has already cleared. The toast is
 * per-viewer chrome, so silencing it is a localStorage convenience — SEEN
 * state is separate and server-side (`useAnnouncements`): dismissing the
 * toast leaves the account menu's unseen count standing until the viewer
 * actually opens the dialog.
 */
const TOAST_ACK_KEY = "whats-new-toast-acknowledged";

/**
 * Mounts the What's New dialog once, in the workspace layout beside the other
 * emitter-driven overlays, and owns the release toast: when unseen entries
 * exist that the viewer has not yet cleared a toast for, ONE persistent toast
 * summarizes them (never one per entry — announcements are curated and rare,
 * and a backlog must not stack). Its action opens the dialog; any close —
 * the action, the dismiss ✕, a swipe — silences those entries' toast for
 * good.
 */
export function WhatsNewDialogMount() {
  const { open, setOpen } = whatsNewDialog.useDialogState();
  const { entries, unseenKeys, markAllSeen } = useAnnouncements();
  const { push: navigate } = useAppNavigate();
  const { state: ack, update: updateAck } = usePersistedState<{
    keys: string[];
  }>(TOAST_ACK_KEY, { keys: [] });

  // Mark everything seen when the dialog closes, not when it opens: on open
  // the discovery query may not have resolved yet (markAllSeen no-ops on
  // empty data), and marking on close still captures every entry the user
  // just read while showing live unseen dots the whole time it's open.
  const closeDialog = () => {
    setOpen(false);
    markAllSeen();
  };

  // No release toast while impersonating: the entries are the member's to
  // discover, and acknowledging one would write this browser's own ack list.
  const { impersonation } = useAuth();
  const unacknowledged = React.useMemo(
    () => (impersonation ? [] : [...unseenKeys].filter((key) => !ack.keys.includes(key))),
    [impersonation, unseenKeys, ack.keys],
  );

  const toastIdRef = React.useRef<string | null>(null);
  // False while the app closes the toast itself (its action, or the entries
  // read elsewhere); true for the viewer's ✕ or swipe.
  const closedByUser = React.useRef(true);
  React.useEffect(() => {
    if (unacknowledged.length === 0) {
      // Nothing left to announce — the entries were read (here or in the
      // menu-opened dialog), so a still-standing toast is stale. Closing it
      // acks its keys via onClose, a no-op for already-seen entries.
      if (toastIdRef.current) {
        closedByUser.current = false;
        toastManager.close(toastIdRef.current);
        toastIdRef.current = null;
      }
      return;
    }
    if (toastIdRef.current) return;

    const keys = unacknowledged;
    const firstUnseen = entries.find((entry) => keys.includes(entry.key));
    const feedKeys = new Set(entries.map((entry) => entry.key));
    toastIdRef.current = toastManager.add({
      // Neutral action toast (no `type`): an announcement, not a status. The
      // gift glyph matches the account menu's What's new row, and the offer
      // wears the spectrum ring — a release announcement, not routine
      // feedback.
      data: { actionColor: "ai", icon: "gift" },
      title: "What's new in SalesTrak",
      description:
        keys.length === 1 && firstUnseen
          ? firstUnseen.title
          : `${keys.length} new updates`,
      // Persistent — the library renders its dismiss ✕ for timeout 0, so the
      // toast stands until the viewer clears it or reads the entries.
      timeout: 0,
      actionProps: {
        children: "See what's new",
        // Toast.Action does not close its toast; close explicitly so the
        // press both opens the dialog and clears (acks) the toast.
        onClick: () => {
          closedByUser.current = false;
          openWhatsNew("toast");
          if (toastIdRef.current) toastManager.close(toastIdRef.current);
        },
      },
      onClose: () => {
        toastIdRef.current = null;
        if (closedByUser.current) {
          trackEvent({
            context: AnalyticsContext.Announcement,
            event: AnnouncementEvent.DismissToast,
            data: { count: keys.length },
          });
        }
        closedByUser.current = true;
        updateAck((current) => ({
          // Prune keys that left the 90-day feed window so the list cannot
          // grow forever.
          keys: [...new Set([...current.keys, ...keys])].filter((key) =>
            feedKeys.has(key),
          ),
        }));
      },
    });
  }, [unacknowledged, entries, updateAck]);

  return (
    <WhatsNewDialog
      entries={entries}
      onNavigate={(route, entryKey) => {
        trackEvent({
          context: AnalyticsContext.Announcement,
          event: AnnouncementEvent.Cta,
          data: { entry: entryKey },
        });
        closeDialog();
        navigate(route);
      }}
      onOpenChange={(next) => (next ? setOpen(true) : closeDialog())}
      open={open}
      unseenKeys={unseenKeys}
    />
  );
}
